import { useCallback, useMemo, useState } from 'react'

import { isNotFound } from '@tribeplatform/gql-client/lib'
import { Action, HomeSpaceType, Page } from '@tribeplatform/gql-client/types'
import {
  useChangeHomepage,
  useHomepage,
  useUnsetHomepage,
} from '@tribeplatform/react-sdk/hooks'
import { UseQueryOptions } from '@tribeplatform/react-sdk/lib'

import { HomepageSettingsFormState } from './types.js'

const useQueryDefaultOptions: UseQueryOptions<Page> = {
  retry(_, error) {
    return !isNotFound(error)
  },
  refetchOnWindowFocus: false,
}

export const useHomepageSettings = () => {
  const {
    data: defaultHomepage,
    error: defaultHomepageError,
    refetch: refetchDefaultHomepage,
    isInitialLoading: isFetchingHomepage,
  } = useHomepage({
    variables: {
      type: HomeSpaceType.Default,
    },
    useQueryOptions: useQueryDefaultOptions,
  })

  const {
    data: newUsersHomepage,
    error: newUserHomepageError,
    refetch: refetchNewUsersHomepage,
    isInitialLoading: isFetchingNewUsersHomepage,
    isFetching: isRefetchingNewUsersHomepage,
  } = useHomepage({
    variables: {
      type: HomeSpaceType.NewUser,
    },
    useQueryOptions: useQueryDefaultOptions,
  })

  const {
    data: returningUsersHomepage,
    error: returningUsersHomepageError,
    refetch: refetchReturningUsersHomepage,
    isInitialLoading: isFetchingReturningUsersHomepage,
    isFetching: isRetchingReturningUsersHomepage,
  } = useHomepage({
    variables: {
      type: HomeSpaceType.ReturningUser,
    },
    useQueryOptions: useQueryDefaultOptions,
  })

  const { mutateAsync: updateHomepage } = useChangeHomepage({})
  const { mutateAsync: unsetHomepage } = useUnsetHomepage({})
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [errors, setErrors] = useState<PromiseSettledResult<Action>[]>([])

  const updateSettings = useCallback(
    async (values: HomepageSettingsFormState) => {
      if (values.defaultHomepage.private) {
        return
      }
      setIsUpdating(true)
      setErrors([])
      try {
        const results = await Promise.allSettled([
          updateHomepage({
            type: HomeSpaceType.Default,
            spaceId: values.defaultHomepage.id,
          }),
          (() => {
            if (!values.newUsersHomepage) {
              return unsetHomepage({
                type: HomeSpaceType.NewUser,
              })
            }
            return updateHomepage({
              type: HomeSpaceType.NewUser,
              spaceId: values.newUsersHomepage.id,
            })
          })(),
          (() => {
            if (!values.returningUsersHomepage) {
              return unsetHomepage({
                type: HomeSpaceType.ReturningUser,
              })
            }
            return updateHomepage({
              type: HomeSpaceType.ReturningUser,
              spaceId: values.returningUsersHomepage.id,
            })
          })(),
        ])
        const errors = results.filter(result => result.status === 'rejected')
        setErrors(errors)
      } finally {
        // Re-populate the homepage query cache
        await Promise.allSettled([
          refetchDefaultHomepage(),
          refetchNewUsersHomepage(),
          refetchReturningUsersHomepage(),
        ])
        setIsUpdating(false)
      }
    },
    [
      refetchDefaultHomepage,
      refetchNewUsersHomepage,
      refetchReturningUsersHomepage,
      unsetHomepage,
      updateHomepage,
    ],
  )

  const isLoading =
    isFetchingHomepage ||
    (isFetchingReturningUsersHomepage && !isRetchingReturningUsersHomepage) ||
    (isFetchingNewUsersHomepage && !isRefetchingNewUsersHomepage)

  return useMemo(
    () => ({
      defaultHomepage: normalizeValueBasedOnFetchError(
        defaultHomepage,
        defaultHomepageError,
      ),
      newUsersHomepage: normalizeValueBasedOnFetchError(
        newUsersHomepage,
        newUserHomepageError,
      ),
      returningUsersHomepage: normalizeValueBasedOnFetchError(
        returningUsersHomepage,
        returningUsersHomepageError,
      ),
      isLoading,
      isUpdating,
      updateSettings,
      errors,
    }),
    [
      defaultHomepage,
      defaultHomepageError,
      errors,
      isLoading,
      isUpdating,
      newUserHomepageError,
      newUsersHomepage,
      returningUsersHomepage,
      returningUsersHomepageError,
      updateSettings,
    ],
  )
}

const normalizeValueBasedOnFetchError = (
  value: Page,
  error: unknown,
): Page | null => (isNotFound(error) ? null : (value ?? null))
